import React, { createContext, useContext } from 'react'
import { LocalStorageKeys, SetStateFn } from '~config/constants'
import {
    ChatFieldsFragment,
    QueryInput,
    useGetChatQuery,
} from '~graphql/generated/graphql'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import useChatPreviewsQuery from './use-chat-previews-query'
import { ChatPreviewsValues } from './use-chat-previews-query'
import { navigate } from 'gatsby'
import { UserRoutes } from '~config/routes'
import { getCleanedQueryParams } from '~utils/get-cleaned-query-params'
import {
    ChatPreviewMessageSource,
    useChatPreviewMessageSource,
} from './chats-list/chat-preview/message-source'

export default function ChatsProvider({
    chatId,
    children,
}: {
    chatId?: string
    children: React.ReactNode
}) {
    const { chatId: chatIdFromUrl } = getCleanedQueryParams()
    const chatIdToUse = chatIdFromUrl || chatId

    const [queryFilters, setQueryFilters] = useLocalStorage<QueryInput[]>(
        LocalStorageKeys.chatsFilters,
        [],
    )
    const [orderBy, setOrderBy] = useLocalStorage<keyof ChatFieldsFragment>(
        LocalStorageKeys.chatsOrderBy,
        'last_message_time',
    )
    const [messageSource, setMessageSource] = useChatPreviewMessageSource(
        LocalStorageKeys.chatsPreviewMessageSource,
        LocalStorageKeys.chatsShowClientMsgsOnly,
    )

    const {
        chatPreviews,
        chatPreviewsCount,
        refetchChatPreviews,
        page,
        setPage,
        isInitialLoad,
        pinnedChats,
        localPinnedChats,
        pinChat,
        unpinChat,
    } = useChatPreviewsQuery({
        queryFilters,
        orderBy,
    })

    const { data: chatData } = useGetChatQuery({
        variables: {
            id: chatIdToUse || '',
        },
        skip: !chatIdToUse,
    })

    const selectedChat = chatData?.chat

    function handleSelectChat(id: string) {
        navigate(UserRoutes.ChatConversation(id))
    }

    return (
        <ChatsContext.Provider
            value={{
                pinnedChats,
                localPinnedChats,
                pinChat,
                unpinChat,
                chatPreviews,
                chatPreviewsCount,
                refetchChatPreviews,
                page,
                setPage,
                isInitialLoad,
                selectedChat,
                handleSelectChat,
                queryFilters,
                setQueryFilters,
                orderBy,
                setOrderBy,
                messageSource,
                setMessageSource,
            }}
        >
            {children}
        </ChatsContext.Provider>
    )
}

type ChatsContextType = {
    selectedChat: ChatFieldsFragment | null | undefined
    handleSelectChat: (id: string) => void
    queryFilters: QueryInput[]
    setQueryFilters: SetStateFn<QueryInput[]>
    orderBy: keyof ChatFieldsFragment
    setOrderBy: SetStateFn<keyof ChatFieldsFragment>
    messageSource: ChatPreviewMessageSource
    setMessageSource: SetStateFn<ChatPreviewMessageSource>
} & ChatPreviewsValues

const ChatsContext = createContext<ChatsContextType>({} as ChatsContextType)

export function useChatsContext() {
    return useContext(ChatsContext)
}
